import React, { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import Slide from '@mui/material/Slide';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Box from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import axiosInstance from '../../../../axios/axiosInstance';
import apiEndpoints from '../../../../axios/api';
import { notify } from '../../../../utils/notification';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const EditVendorModal = ({ open, handleClose, vendor, onSaved }) => {
    const [formData, setFormData] = useState({
        name: '',
        category: '',
        email: '',
        representative: '',
        phone: '',
        city: '',
    });
    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (vendor) {
            setFormData({
                name: vendor.name || '',
                category: vendor.category || '',
                email: vendor.email || '',
                representative: vendor.representative === '-' ? '' : vendor.representative || '',
                phone: vendor.phone === '-' ? '' : vendor.phone || '',
                city: vendor.city === '-' ? '' : vendor.city || '',
            });
            setErrors({});
        }
    }, [vendor, open]);

    const validateForm = () => {
        const newErrors = {};
        if (!formData.name.trim()) newErrors.name = 'Vendor name is required';
        if (!formData.category.trim()) newErrors.category = 'Category is required';
        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = 'Invalid email format';
        }
        if (!formData.phone.trim()) newErrors.phone = 'Phone is required';

        return newErrors;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear error for this field when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const handleSubmit = async () => {
        const newErrors = validateForm();
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        setIsLoading(true);
        try {
            // Saved through the API the society portal uses. The form used to
            // log to the console and close, so nothing was ever stored.
            // Category is a name here; it is left to the society portal's own
            // vendor form, which has the category picker.
            await axiosInstance.put(
                apiEndpoints.SOCIETY_UPDATE_VENDOR_API.replace(':id', vendor.id),
                {
                    business_name: formData.name,
                    contact_person: formData.representative || null,
                    email: formData.email,
                    phone: String(formData.phone).replace(/\D/g, '')
                }
            );
            notify('success', 'Vendor updated');
            if (typeof onSaved === 'function') onSaved();
            handleClose();
        } catch (error) {
            notify('error', error?.response?.data?.message || 'Could not update the vendor');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Dialog
            open={open}
            TransitionComponent={Transition}
            keepMounted
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle
                sx={{
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    color: 'white',
                    position: 'relative',
                    overflow: 'hidden',
                    p: 3,
                }}
            >
                <svg
                    width="100%"
                    height="100%"
                    viewBox="0 0 400 300"
                    xmlns="http://www.w3.org/2000/svg"
                    style={{ position: 'absolute', top: 0, left: 0, opacity: 0.1 }}
                >
                    <circle cx="50" cy="50" r="40" fill="white" />
                    <circle cx="350" cy="250" r="60" fill="white" />
                    <circle cx="150" cy="200" r="30" fill="white" />
                </svg>
                <Box sx={{ position: 'relative', zIndex: 1, fontSize: '1.25rem', fontWeight: 600 }}>
                    Edit Vendor
                </Box>
            </DialogTitle>

            <DialogContent sx={{ p: 3, backgroundColor: '#f8fafc' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                    <TextField
                        label="Vendor Name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        size="small"
                        fullWidth
                        error={!!errors.name}
                        helperText={errors.name}
                        disabled={isLoading}
                    />

                    <TextField
                        label="Category"
                        name="category"
                        value={formData.category}
                        onChange={handleChange}
                        size="small"
                        fullWidth
                        error={!!errors.category}
                        helperText={errors.category}
                        disabled={isLoading}
                    />

                    <TextField
                        label="Email Address"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        size="small"
                        fullWidth
                        error={!!errors.email}
                        helperText={errors.email}
                        disabled={isLoading}
                    />

                    <TextField
                        label="Representative"
                        name="representative"
                        value={formData.representative}
                        onChange={handleChange}
                        size="small"
                        fullWidth
                        disabled={isLoading}
                    />

                    <TextField
                        label="Phone Number"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        size="small"
                        fullWidth
                        error={!!errors.phone}
                        helperText={errors.phone}
                        disabled={isLoading}
                    />

                    <TextField
                        label="City"
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        size="small"
                        fullWidth
                        disabled={isLoading}
                    />
                </Box>
            </DialogContent>

            <DialogActions sx={{ p: 2, gap: 1 }}>
                <Button
                    variant="outlined"
                    onClick={handleClose}
                    disabled={isLoading}
                    sx={{ textTransform: 'capitalize' }}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={isLoading}
                    sx={{
                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                        textTransform: 'capitalize',
                    }}
                >
                    {isLoading ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
                    Save Changes
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EditVendorModal;
